import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  Ellipsis,
  Home,
  ListTodo,
  Network,
  Plus,
  Settings,
  Users,
} from "lucide-react";
import { CanAccess, useGetIdentity, useTranslate } from "ra-core";
import type { AppRole } from "../types";
import { getMobileOverflowEntries, type MobileNavEntry } from "./navEntries";
import { Link, matchPath, useLocation, useMatch } from "react-router";
import { AlmikiaPage } from "../almikia/AlmikiaPage";
import { ContactCreateSheet } from "../contacts/ContactCreateSheet";
import { useState } from "react";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";
import {
  MOBILE_NAV_BAR_HEIGHT,
  MOBILE_NAV_BOTTOM_INSET,
  MOBILE_NAV_IOS_PWA_EXTRA,
  isIOSStandalonePwa,
} from "./mobileNavClearance";

/**
 * Floating bottom tab bar (mockup A): a rounded navy pill inset from the
 * screen edges, same 5 entries and CanAccess gating as before. Every label —
 * including the longest, "Configuración" — must fit on its tab with no
 * clipping at 390px: see <NavigationButton> for the two Tailwind
 * specificity/inheritance fixes that make that possible.
 */
export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/tasks/*", location.pathname)) {
    currentPath = "/tasks";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (matchPath("/almikia/*", location.pathname)) {
    currentPath = "/almikia";
  } else {
    currentPath = false;
  }

  const { identity } = useGetIdentity();
  const overflow = identity?.role
    ? getMobileOverflowEntries(identity.role as AppRole)
    : [];

  const iosExtra = isIOSStandalonePwa() ? MOBILE_NAV_IOS_PWA_EXTRA : 0;

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed left-4 right-4 z-50 bg-sidebar text-sidebar-foreground rounded-full shadow-lg"
      style={{
        bottom: MOBILE_NAV_BOTTOM_INSET,
        // iOS bug: even though viewport is set correctly, the bottom safe area inset is
        // not accounted for, so we manually pad the bar to keep it clear of the home bar.
        paddingBottom: iosExtra,
        height: MOBILE_NAV_BAR_HEIGHT + iosExtra,
      }}
    >
      <div className="flex justify-around items-stretch h-full px-1">
        <NavigationButton
          href="/"
          Icon={Home}
          label={translate("ra.page.dashboard")}
          isActive={currentPath === "/"}
        />
        <CanAccess resource="contacts" action="list">
          <NavigationButton
            href="/contacts"
            Icon={Users}
            label={translate("resources.contacts.name", {
              smart_count: 2,
            })}
            isActive={currentPath === "/contacts"}
          />
        </CanAccess>
        <CanAccess resource="contacts" action="create">
          <CreateButton />
        </CanAccess>
        <CanAccess resource="tasks" action="list">
          <NavigationButton
            href="/tasks"
            Icon={ListTodo}
            label={translate("resources.tasks.name", { smart_count: 2 })}
            isActive={currentPath === "/tasks"}
          />
        </CanAccess>
        {/* Almikia Epic EO (TASK-098): every one of the 8 roles reaches the
            Almikia section (org chart, and culture later, TASK-099). */}
        {/* TASK-051: roles with extra destinations get "Mas" (which also
            holds Almikia, keeping the pill at 6 slots); the others keep the
            Almikia tab. */}
        {overflow.length > 0 ? (
          <MoreButton entries={overflow} pathname={location.pathname} />
        ) : (
          <CanAccess resource="org_chart_people" action="list">
            <NavigationButton
              href={AlmikiaPage.path}
              Icon={Network}
              label={translate("crm.almikia.nav_label")}
              isActive={currentPath === "/almikia"}
            />
          </CanAccess>
        )}
        <SettingsButton />
      </div>
    </nav>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button
    asChild
    variant="ghost"
    className={cn(
      // min-w-0 lets this flex item shrink below its label's unwrapped width,
      // which is required for the label span's break-words below to actually
      // kick in instead of the item pushing itself wider than its share.
      "flex-col gap-0 h-full min-w-0 flex-1 rounded-full py-1",
      // The base Button size adds `has-[>svg]:px-3` (12px): its `:has(>svg)`
      // compound selector out-specifies a plain `px-0.5`, so the override
      // must match it (`has-[>svg]:px-0.5`) to actually win in the cascade.
      "px-0.5 has-[>svg]:px-0.5",
      "focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-0",
      isActive
        ? "text-brand-yellow bg-sidebar-accent/30"
        : "text-sidebar-foreground/70",
    )}
  >
    <Link to={href} aria-current={isActive ? "page" : undefined}>
      <Icon className="size-4 shrink-0" />
      <span
        className={cn(
          // The base Button is `whitespace-nowrap`, which is inherited by
          // this span. Setting `break-words` alone has no effect while
          // white-space still forbids wrapping, so `whitespace-normal` must
          // be declared directly on the label itself. Font size stays >=
          // 12.5px per the design-system checklist even at 2 lines.
          "w-full text-[13px] leading-[14px] text-center whitespace-normal break-words",
          isActive && "font-bold",
        )}
      >
        {label}
      </span>
    </Link>
  </Button>
);

const CreateButton = () => {
  const translate = useTranslate();
  const contact_id = useMatch("/contacts/:id/*")?.params.id;
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);

  return (
    <>
      <ContactCreateSheet
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
      />
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={contact_id}
      />
      <TaskCreateSheet
        open={taskCreateOpen}
        onOpenChange={setTaskCreateOpen}
        contact_id={contact_id}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            className="flex-col gap-0 h-full min-w-0 flex-1 rounded-full py-1 px-1 has-[>svg]:px-1 focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-0"
            aria-label={translate("ra.action.create")}
          >
            <Plus className="size-4 shrink-0" />
            <span className="w-full text-[13px] leading-[14px] text-center">
              {translate("ra.action.create")}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setContactCreateOpen(true);
            }}
          >
            {translate("resources.contacts.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setNoteCreateOpen(true);
            }}
          >
            {translate("resources.notes.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setTaskCreateOpen(true);
            }}
          >
            {translate("resources.tasks.forcedCaseName")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

/** "Mas" overflow tab: a menu of the extra destinations, rows 48px tall. */
const MoreButton = ({
  entries,
  pathname,
}: {
  entries: MobileNavEntry[];
  pathname: string;
}) => {
  const translate = useTranslate();
  const isActive = entries.some((entry) =>
    matchPath(`${entry.href}/*`, pathname),
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          aria-label={translate("crm.navigation.more")}
          className={cn(
            "flex-col gap-0 h-full min-w-0 flex-1 rounded-full py-1 px-0.5 has-[>svg]:px-0.5",
            "focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-0",
            isActive
              ? "text-brand-yellow bg-sidebar-accent/30"
              : "text-sidebar-foreground/70",
          )}
        >
          <Ellipsis className="size-4 shrink-0" />
          <span
            className={cn(
              "w-full text-[13px] leading-[14px] text-center whitespace-normal break-words",
              isActive && "font-bold",
            )}
          >
            {translate("crm.navigation.more")}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {entries.map(({ id, href, labelKey, smartCount, Icon }) => (
          <DropdownMenuItem key={id} asChild className="h-12 px-4 text-base">
            <Link
              to={href}
              aria-current={
                matchPath(`${href}/*`, pathname) ? "page" : undefined
              }
            >
              <Icon />
              {translate(labelKey, { smart_count: smartCount })}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SettingsButton = () => {
  const translate = useTranslate();
  const location = useLocation();
  const isActive = !!matchPath("/settings", location.pathname);

  return (
    <NavigationButton
      href="/settings"
      Icon={Settings}
      label={translate("crm.settings.title")}
      isActive={isActive}
    />
  );
};
